/**
 * Users admin tab — with search, filter, pagination, and stats.
 * Adopted search/filter/stats pattern from QRI. Core security unchanged.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getWithMeta, api, ApiError } from '../api/client';
import { Modal } from './admin-ui';

type RoleCode = 'admin' | 'super_user' | 'analyst' | 'viewer';

interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  status: 'Active' | 'Disabled';
  failed_logins: number;
  last_login_at: string | null;
  created_at: string;
  roles: RoleCode[];
}

interface UserStats {
  total_users: number;
  active_users: number;
  disabled_users: number;
  logged_in_today: number;
  logged_in_week: number;
  new_this_month: number;
  users_with_failed_logins: number;
}

const ROLE_LABELS: Record<RoleCode, string> = {
  admin: 'Admin',
  super_user: 'Super User',
  analyst: 'Analyst',
  viewer: 'Viewer',
};

const PAGE_SIZE = 20;

export function UsersAdminTab() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [resettingPwOf, setResettingPwOf] = useState<AdminUser | null>(null);

  // Search / filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [offset, setOffset] = useState(0);

  // Build query string
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (statusFilter) params.set('status', statusFilter);
  if (roleFilter) params.set('role', roleFilter);
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String(offset));

  // Users list — backend may return array (old router) OR { data, meta } (new router)
  // We handle both shapes safely.
  const usersQuery = useQuery<AdminUser[] | { data: AdminUser[]; meta: { total: number } }>({
    queryKey: ['admin-users', search, statusFilter, roleFilter, offset],
    // getWithMeta keeps meta.total; api.get strips the envelope to the page of rows, so the pager never showed.
    queryFn: async () => {
      const r = await getWithMeta<AdminUser[]>(`/admin/users?${params.toString()}`);
      return { data: r.data ?? [], meta: { total: Number(r.meta['total'] ?? (r.data ?? []).length) } };
    },
  });

  // Stats — only available if new backend router is deployed; fail silently if not
  const statsQuery = useQuery<UserStats>({
    queryKey: ['admin-user-stats'],
    queryFn: () => api.get<UserStats>('/admin/users/stats'),
    retry: false,
  });

  // Normalise response — works whether backend returns array or { data, meta }
  const rawData = usersQuery.data;
  const users: AdminUser[] = Array.isArray(rawData)
    ? rawData
    : (rawData as { data: AdminUser[] } | undefined)?.data ?? [];
  const total: number = Array.isArray(rawData)
    ? rawData.length
    : (rawData as { meta?: { total?: number } } | undefined)?.meta?.total ?? users.length;

  const stats = statsQuery.isSuccess ? statsQuery.data : null;

  function resetFilters() {
    setSearch('');
    setStatusFilter('');
    setRoleFilter('');
    setOffset(0);
  }

  return (
    <div>
      {/* Stats row — only shown if stats endpoint exists */}
      {stats && (
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Total users', value: stats.total_users },
            { label: 'Active', value: stats.active_users },
            { label: 'Logged in today', value: stats.logged_in_today },
            { label: 'New this month', value: stats.new_this_month },
          ].map((s) => (
            <div key={s.label} className="border border-ink-200 rounded p-3">
              <div className="text-2xl font-semibold text-ink">{s.value ?? 0}</div>
              <div className="text-xs text-ink-500 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search + filters */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <input
          type="search"
          placeholder="Search by email or name…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
          className="px-3 py-1.5 border border-ink-200 rounded text-sm w-64 focus:outline-none focus:border-accent"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }}
          className="px-3 py-1.5 border border-ink-200 rounded text-sm focus:outline-none focus:border-accent"
        >
          <option value="">All statuses</option>
          <option value="Active">Active</option>
          <option value="Disabled">Disabled</option>
        </select>
        <select
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setOffset(0); }}
          className="px-3 py-1.5 border border-ink-200 rounded text-sm focus:outline-none focus:border-accent"
        >
          <option value="">All roles</option>
          <option value="admin">Admin</option>
          <option value="super_user">Super User</option>
          <option value="analyst">Analyst</option>
          <option value="viewer">Viewer</option>
        </select>
        {(search || statusFilter || roleFilter) && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs text-ink-500 hover:text-ink-700 underline"
          >
            Clear filters
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-ink-500">
            {total} user{total !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="text-xs px-3 py-1.5 bg-accent text-white rounded hover:bg-accent-hover"
          >
            Add user
          </button>
        </div>
      </div>

      {/* Table */}
      {usersQuery.isLoading ? (
        <div className="p-6 text-ink-500 italic text-sm">Loading users…</div>
      ) : usersQuery.isError ? (
        <div className="p-6 text-red-700 text-sm">Failed to load users.</div>
      ) : users.length === 0 ? (
        <div className="p-6 text-ink-500 italic text-sm text-center">
          {search || statusFilter || roleFilter
            ? 'No users match your filters.'
            : 'No users yet.'}
        </div>
      ) : (
        <div className="border border-ink-200 rounded overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-ink-50 text-xs text-ink-500 uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Display name</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Roles</th>
                <th className="px-3 py-2 text-left">Last login</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-3 py-2">{u.display_name}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${
                        u.status === 'Active'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-ink-100 text-ink-500'
                      }`}
                    >
                      {u.status}
                    </span>
                    {u.failed_logins > 0 && (
                      <span
                        className="ml-2 text-xs text-amber-700"
                        title="Failed login attempts since last successful login"
                      >
                        {u.failed_logins} failed
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {u.roles.map((r) => (
                      <span
                        key={r}
                        className="inline-block mr-1 px-2 py-0.5 bg-ink-50 text-ink-700 rounded"
                      >
                        {ROLE_LABELS[r] ?? r}
                      </span>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-500">
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleString()
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setEditing(u)}
                      className="text-accent hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setResettingPwOf(u)}
                      className="text-accent hover:underline"
                    >
                      Reset password
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination — only shown when there are multiple pages */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-3 text-xs text-ink-500">
          <span>
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="px-3 py-1 border border-ink-200 rounded hover:bg-ink-50 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="px-3 py-1 border border-ink-200 rounded hover:bg-ink-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showAdd && (
        <AddUserModal
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            queryClient.invalidateQueries({ queryKey: ['admin-user-stats'] });
            setShowAdd(false);
          }}
        />
      )}
      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            setEditing(null);
          }}
        />
      )}
      {resettingPwOf && (
        <ResetPasswordModal
          user={resettingPwOf}
          onClose={() => setResettingPwOf(null)}
          onDone={() => setResettingPwOf(null)}
        />
      )}
    </div>
  );
}

function AddUserModal({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: () => void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState<RoleCode[]>(['viewer']);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.post('/admin/users', {
        email,
        display_name: displayName,
        password,
        roles,
      }),
    onSuccess: () => onAdded(),
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.message);
      else setError('Failed to add user');
    },
  });

  return (
    <Modal open={true} title="Add user" onClose={onClose}>
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm text-ink-700">Email</span>
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-ink-200 rounded text-sm"
            autoFocus
          />
        </label>
        <label className="block">
          <span className="text-sm text-ink-700">Display name</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-ink-200 rounded text-sm"
          />
        </label>
        <label className="block">
          <span className="text-sm text-ink-700">Initial password (12+ chars)</span>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-ink-200 rounded text-sm font-mono"
          />
        </label>
        <fieldset>
          <legend className="text-sm text-ink-700">Roles</legend>
          <div className="flex gap-3 mt-1">
            {(['admin', 'super_user', 'analyst', 'viewer'] as RoleCode[]).map((r) => (
              <label key={r} className="text-sm flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={roles.includes(r)}
                  onChange={(e) => {
                    if (e.target.checked) setRoles([...roles, r]);
                    else setRoles(roles.filter((x) => x !== r));
                  }}
                />
                {ROLE_LABELS[r]}
              </label>
            ))}
          </div>
        </fieldset>
        {error && <div className="text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 text-sm border border-ink-200 rounded hover:bg-ink-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={
              !email ||
              !displayName ||
              !password ||
              roles.length === 0 ||
              mut.isPending
            }
            onClick={() => mut.mutate()}
            className="px-3 py-1 text-sm bg-accent text-white rounded hover:bg-accent-hover disabled:opacity-50"
          >
            {mut.isPending ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState(user.display_name);
  const [status, setStatus] = useState(user.status);
  const [roles, setRoles] = useState<RoleCode[]>(user.roles);
  const [error, setError] = useState<string | null>(null);

  const patchMut = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}`, {
        display_name: displayName,
        status,
      }),
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  const rolesMut = useMutation({
    mutationFn: () =>
      api.post(`/admin/users/${user.id}/roles`, { roles }),
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  async function save() {
    setError(null);
    try {
      await patchMut.mutateAsync();
      await rolesMut.mutateAsync();
      onSaved();
    } catch {
      // error handled in onError handlers above
    }
  }

  return (
    <Modal open={true} title={`Edit user: ${user.email}`} onClose={onClose}>
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm text-ink-700">Display name</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-ink-200 rounded text-sm"
          />
        </label>
        <label className="block">
          <span className="text-sm text-ink-700">Status</span>
          <select
            value={status}
            onChange={(e) =>
              setStatus(e.target.value as 'Active' | 'Disabled')
            }
            className="mt-1 w-full px-3 py-2 border border-ink-200 rounded text-sm"
          >
            <option value="Active">Active</option>
            <option value="Disabled">Disabled (cannot log in)</option>
          </select>
        </label>
        <fieldset>
          <legend className="text-sm text-ink-700">Roles</legend>
          <div className="flex gap-3 mt-1">
            {(['admin', 'super_user', 'analyst', 'viewer'] as RoleCode[]).map((r) => (
              <label key={r} className="text-sm flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={roles.includes(r)}
                  onChange={(e) => {
                    if (e.target.checked) setRoles([...roles, r]);
                    else setRoles(roles.filter((x) => x !== r));
                  }}
                />
                {ROLE_LABELS[r]}
              </label>
            ))}
          </div>
        </fieldset>
        {error && <div className="text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 text-sm border border-ink-200 rounded hover:bg-ink-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={
              roles.length === 0 ||
              patchMut.isPending ||
              rolesMut.isPending
            }
            onClick={save}
            className="px-3 py-1 text-sm bg-accent text-white rounded hover:bg-accent-hover disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: () => void;
}) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      api.post(`/admin/users/${user.id}/password`, { password: pw }),
    onSuccess: () => setDone(true),
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  if (done) {
    return (
      <Modal open={true} title="Password reset" onClose={onDone}>
        <div className="space-y-3">
          <p className="text-sm text-green-700">
            Password reset for <strong>{user.email}</strong>. All active
            sessions for this user have been revoked.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onDone}
              className="px-3 py-1 text-sm bg-accent text-white rounded"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={true} title={`Reset password: ${user.email}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-ink-500">
          The user will need to log in again with this new password. All
          active sessions for this user will be revoked.
        </p>
        <label className="block">
          <span className="text-sm text-ink-700">New password (12+ chars)</span>
          <input
            type="text"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-ink-200 rounded text-sm font-mono"
            autoFocus
          />
        </label>
        {error && <div className="text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 text-sm border border-ink-200 rounded hover:bg-ink-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={pw.length < 12 || mut.isPending}
            onClick={() => mut.mutate()}
            className="px-3 py-1 text-sm bg-accent text-white rounded hover:bg-accent-hover disabled:opacity-50"
          >
            {mut.isPending ? 'Resetting…' : 'Reset password'}
          </button>
        </div>
      </div>
    </Modal>
  );
}